import {
  AgentChatMessageId,
  AgentChatThreadId,
  DateTimeString,
  HexId,
  HexVersionId,
  UserId,
  stableEmptyArray,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { sortBy } from "lodash";

import { AgentChatMessageFragment } from "../../components/agent/hex-project-shared/messages/AgentChatMessage.generated";
import {
  AgentChatMessage,
  AgentChatThread,
} from "../../components/agent/shared/agentChatMessage.types";
import {
  messageFragmentToAgentChatMessage,
  threadFragmentToAgentChatThread,
} from "../../components/agent/shared/agentChatMessageTypeUtils";
import { mergeAgentChatMessagesWithRevisions } from "../../components/agent/shared/mergeAgentChatMessagesWithRevisions";
import {
  AgentChatThreadFragment,
  AgentChatThreadListItemFragment,
} from "../../components/agent/shared/sharedThreadManagementUtils.generated";
import {
  AgentChatThreadStatus,
  AgentChatThreadType,
} from "../../generated/graphqlTypes";
import { RootState } from "../store";
import { createShallowResultSelector } from "../utils/index.js";

type TaoProjectAgentState = {
  threadsMap: Record<AgentChatThreadId, AgentChatThread>;
  isLoadingThreads: boolean;
  hasMoreThreads: boolean;
  isLoadingMoreThreads: boolean;
  selectedThreadId: AgentChatThreadId | null;
  isLoadingSelectedThread: boolean;
  loadMoreThreadsTrigger: number;
  dismissedAskUserMessageIds: Record<AgentChatMessageId, true>;
};

const initialState: TaoProjectAgentState = {
  threadsMap: {},
  isLoadingThreads: false,
  hasMoreThreads: false,
  isLoadingMoreThreads: false,
  selectedThreadId: null,
  isLoadingSelectedThread: false,
  loadMoreThreadsTrigger: 0,
  dismissedAskUserMessageIds: {},
};

export const taoProjectAgentSlice = createSlice({
  name: "taoProject",
  initialState,
  reducers: {
    resetTaoProject: () => initialState,
    setTaoProjectThreads: (
      state: TaoProjectAgentState,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );
      state.threadsMap = {};
      for (const thread of threads) {
        state.threadsMap[thread.id] = thread;
      }
    },
    appendTaoProjectThreads: (
      state: TaoProjectAgentState,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );

      for (const thread of threads) {
        const existing = state.threadsMap[thread.id];
        state.threadsMap[thread.id] =
          existing != null
            ? {
                ...thread,
                status: mergeAppendedThreadStatus(
                  existing.status,
                  thread.status,
                ),
                agentMessages: existing.agentMessages,
                messageCount: Math.max(
                  thread.messageCount,
                  existing.messageCount,
                  existing.agentMessages.length,
                ),
                startCursor: existing.startCursor,
                hasEarlierMessages: existing.hasEarlierMessages,
              }
            : thread;
      }
    },
    setHasMoreTaoProjectThreads: (
      state: TaoProjectAgentState,
      action: PayloadAction<boolean>,
    ) => {
      state.hasMoreThreads = action.payload;
    },
    setIsLoadingTaoProjectThreads: (
      state: TaoProjectAgentState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingThreads = action.payload;
    },
    setIsLoadingMoreTaoProjectThreads: (
      state: TaoProjectAgentState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingMoreThreads = action.payload;
    },
    triggerLoadMoreTaoProjectThreads: (state: TaoProjectAgentState) => {
      state.loadMoreThreadsTrigger = state.loadMoreThreadsTrigger + 1;
    },
    setSelectedTaoProjectThreadId: (
      state: TaoProjectAgentState,
      action: PayloadAction<AgentChatThreadId | null>,
    ) => {
      if (action.payload === state.selectedThreadId) {
        return;
      }
      state.selectedThreadId = action.payload;
      state.isLoadingSelectedThread = action.payload != null;
    },
    setMessagesForTaoProjectThread: (
      state: TaoProjectAgentState,
      action: PayloadAction<{
        thread: AgentChatThreadFragment;
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, thread } = action.payload;

      const messages = thread.agentMessages.edges.map((e) => e.node);
      setThreadMessages(state, {
        threadId: thread.id,
        messages,
        currentUserId,
      });

      const storedThread = state.threadsMap[thread.id];
      if (storedThread != null) {
        storedThread.auto = thread.auto;
        storedThread.model = thread.model;
        storedThread.lastUsedEffortLevel =
          thread.lastUsedEffortLevel ?? storedThread.lastUsedEffortLevel;
        storedThread.lastUsedSpeed =
          thread.lastUsedSpeed ?? storedThread.lastUsedSpeed;
        storedThread.messageCount = Math.max(
          thread.messageCount,
          storedThread.messageCount,
          storedThread.agentMessages.length,
        );
        storedThread.hasEarlierMessages =
          thread.agentMessages.pageInfo?.hasPreviousPage ?? false;
        storedThread.startCursor =
          storedThread.startCursor ??
          thread.agentMessages.pageInfo?.startCursor ??
          null;
      }

      state.isLoadingSelectedThread = false;
    },
    prependMessagesForTaoProjectThread: (
      state: TaoProjectAgentState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        messages: readonly AgentChatMessageFragment[];
        hasPreviousPage: boolean;
        startCursor: string | null;
      }>,
    ) => {
      const { hasPreviousPage, messages, startCursor, threadId } =
        action.payload;
      const thread = state.threadsMap[threadId];
      if (thread == null) return;

      const olderMessages = messages.map(messageFragmentToAgentChatMessage);
      const threadCopy = { ...thread };
      threadCopy.agentMessages = mergeAgentChatMessagesWithRevisions(
        olderMessages,
        threadCopy.agentMessages,
      );
      threadCopy.hasEarlierMessages = hasPreviousPage;
      threadCopy.startCursor = startCursor;
      threadCopy.messageCount = Math.max(
        threadCopy.messageCount,
        threadCopy.agentMessages.length,
      );
      state.threadsMap[threadId] = threadCopy;
    },
    setIsLoadingSelectedTaoProjectThread: (
      state: TaoProjectAgentState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingSelectedThread = action.payload;
    },
    addMessagesToTaoProjectThread: (
      state: TaoProjectAgentState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        messages: readonly AgentChatMessageFragment[];
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, messages, threadId } = action.payload;

      const thread = ensureThreadExists(state, threadId, currentUserId);

      const hasSubagentMessages = messages.some((m) => m.subagentType != null);
      const existingThread = state.threadsMap[threadId];
      if (
        existingThread?.status === "IDLE_STOP_REQUESTED" &&
        !hasSubagentMessages
      ) {
        return;
      }

      const threadCopy = { ...thread };
      const newMessages: AgentChatMessage[] = messages.map(
        messageFragmentToAgentChatMessage,
      );

      threadCopy.agentMessages = mergeAgentChatMessagesWithRevisions(
        threadCopy.agentMessages,
        newMessages,
      );
      threadCopy.messageCount = Math.max(
        threadCopy.messageCount,
        threadCopy.agentMessages.length,
      );

      state.threadsMap[threadId] = threadCopy;
    },
    updateTaoProjectThreadMetadata: (
      state: TaoProjectAgentState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        updates: {
          title: string | null;
          status: AgentChatThreadStatus;
          autoSaveHexVersionId: HexVersionId | null;
          autoSaveFailed?: boolean | null;
        };
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, threadId, updates } = action.payload;
      const thread = ensureThreadExists(state, threadId, currentUserId);

      const existingStatus = thread.status;
      const newStatus =
        existingStatus === "IDLE_STOP_REQUESTED" && updates.status === "IDLE"
          ? "IDLE_STOP_REQUESTED"
          : updates.status;

      const threadCopy = { ...thread, status: newStatus };

      if (updates.title) {
        threadCopy.title = updates.title;
      }

      if (updates.autoSaveHexVersionId) {
        threadCopy.autoSaveHexVersionId = updates.autoSaveHexVersionId;
      }

      state.threadsMap[threadId] = threadCopy;
    },
    removeTaoProjectThread: (
      state: TaoProjectAgentState,
      action: PayloadAction<{ threadId: AgentChatThreadId }>,
    ) => {
      delete state.threadsMap[action.payload.threadId];
    },
    dismissTaoProjectAskUserMessage: (
      state: TaoProjectAgentState,
      action: PayloadAction<AgentChatMessageId>,
    ) => {
      state.dismissedAskUserMessageIds[action.payload] = true;
    },
    undismissTaoProjectAskUserMessage: (
      state: TaoProjectAgentState,
      action: PayloadAction<AgentChatMessageId>,
    ) => {
      delete state.dismissedAskUserMessageIds[action.payload];
    },
  },
});

const selectThreads = createShallowResultSelector(
  [(state: RootState) => state.taoProject.threadsMap],
  (threadsMap) => sortBy(Object.values(threadsMap), "createdDate").reverse(),
);

const selectThreadMessages = (
  state: RootState,
  threadId: AgentChatThreadId | null,
): readonly AgentChatMessage[] => {
  if (!threadId) return stableEmptyArray<AgentChatMessage>();

  const thread = state.taoProject.threadsMap[threadId];
  if (!thread) return stableEmptyArray<AgentChatMessage>();

  return thread.agentMessages;
};

export const taoProjectAgentSelectors = {
  selectIsLoadingThreads: (state: RootState) =>
    state.taoProject.isLoadingThreads,
  selectIsLoadingSelectedThread: (state: RootState) =>
    state.taoProject.isLoadingSelectedThread,
  selectSelectedThreadId: (state: RootState) =>
    state.taoProject.selectedThreadId,
  selectIsLoadingMoreThreads: (state: RootState) =>
    state.taoProject.isLoadingMoreThreads,
  selectHasMoreThreads: (state: RootState) => state.taoProject.hasMoreThreads,
  selectLoadMoreThreadsTrigger: (state: RootState) =>
    state.taoProject.loadMoreThreadsTrigger,
  selectThreads,
  selectSelectedThread: (state: RootState) => {
    const selectedThreadId = state.taoProject.selectedThreadId;
    return selectedThreadId
      ? state.taoProject.threadsMap[selectedThreadId] || null
      : null;
  },
  selectIsSelectedThreadRunningOrSaving: (state: RootState) => {
    const selectedThreadId = state.taoProject.selectedThreadId;
    if (selectedThreadId == null) return false;

    const selectedThread = state.taoProject.threadsMap[selectedThreadId];
    return (
      selectedThread?.status === "RUNNING" ||
      selectedThread?.status === "SAVING_VERSION"
    );
  },
  selectSelectedThreadMessages: (state: RootState) => {
    const selectedThreadId = state.taoProject.selectedThreadId;
    return selectThreadMessages(state, selectedThreadId);
  },
  selectDismissedAskUserMessageIds: (state: RootState) =>
    state.taoProject.dismissedAskUserMessageIds,
};

export const taoProjectAgentReducer = taoProjectAgentSlice.reducer;

export const {
  addMessagesToTaoProjectThread,
  appendTaoProjectThreads,
  dismissTaoProjectAskUserMessage,
  prependMessagesForTaoProjectThread,
  removeTaoProjectThread,
  resetTaoProject,
  setHasMoreTaoProjectThreads,
  setIsLoadingMoreTaoProjectThreads,
  setIsLoadingSelectedTaoProjectThread,
  setIsLoadingTaoProjectThreads,
  setMessagesForTaoProjectThread,
  setSelectedTaoProjectThreadId,
  setTaoProjectThreads,
  triggerLoadMoreTaoProjectThreads,
  undismissTaoProjectAskUserMessage,
  updateTaoProjectThreadMetadata,
} = taoProjectAgentSlice.actions;

function mergeAppendedThreadStatus(
  existingStatus: AgentChatThreadStatus,
  incomingStatus: AgentChatThreadStatus,
): AgentChatThreadStatus {
  if (
    (existingStatus === "RUNNING" || existingStatus === "SAVING_VERSION") &&
    incomingStatus === "IDLE"
  ) {
    return existingStatus;
  }
  return incomingStatus;
}

const createOptimisticThread = (
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
): AgentChatThread => {
  return {
    id: threadId,
    hexId: null,
    hexVersionId: null,
    title: null,
    createdDate: new Date().toISOString() as DateTimeString,
    status: "IDLE" as AgentChatThreadStatus,
    type: "CODE_PROJECT" as AgentChatThreadType,
    autoSaveHexVersionId: null,
    agentMessages: [],
    editsToReview: [],
    userId: currentUserId,
    messageCount: 0,
    auto: true,
    model: null,
    lastUsedEffortLevel: null,
    lastUsedSpeed: null,
    startCursor: null,
  };
};

const ensureThreadExists = (
  state: TaoProjectAgentState,
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
  hexId: HexId | null = null,
): AgentChatThread => {
  let thread = state.threadsMap[threadId];

  if (!thread) {
    thread = createOptimisticThread(threadId, currentUserId);
    state.threadsMap[threadId] = thread;
  } else if (thread.hexId == null && hexId != null) {
    thread = { ...thread, hexId };
    state.threadsMap[threadId] = thread;
  }

  return thread;
};

function setThreadMessages(
  state: TaoProjectAgentState,
  {
    currentUserId,
    messages,
    threadId,
  }: {
    threadId: AgentChatThreadId;
    messages: readonly AgentChatMessageFragment[];
    currentUserId: UserId | null;
  },
): void {
  const thread = ensureThreadExists(state, threadId, currentUserId);
  const copy = { ...thread };

  const newMessages = messages.map(messageFragmentToAgentChatMessage);
  copy.agentMessages = mergeAgentChatMessagesWithRevisions(
    copy.agentMessages,
    newMessages,
  );
  copy.messageCount = Math.max(copy.messageCount, copy.agentMessages.length);

  state.threadsMap[threadId] = copy;
}
